import { createReducer, on } from '@ngrx/store';
import { NotificationPreference } from '../../models/notification-preference';
import { NotificationType } from '../../models/notification-type';
import {
  getNotificationPreferences,
  getNotificationPreferencesFailure,
  getNotificationPreferencesSuccess,
  getNotificationTypes,
  getNotificationTypesFailure,
  getNotificationTypesSuccess,
  saveNotificationPreferences,
  saveNotificationPreferencesFailure,
  saveNotificationPreferencesSuccess,
} from '../actions/notification-preference.actions';

export const notificationPreferenceFeatureKey = 'notification-preferences';

export interface NotificationPreferenceState {
  notificationPreferences: NotificationPreference | null;
  notificationTypes: NotificationType[] | null;
  error: unknown;
  // Kept apart from `error` so a failed types or preferences load cannot surface on the save
  // surfaces as a rejection the user never caused.
  saveError: unknown;
  isSaving: boolean;
  isLoading: boolean;
}

export const initialState: NotificationPreferenceState = {
  notificationPreferences: null,
  notificationTypes: null,
  error: null,
  saveError: null,
  isSaving: false,
  isLoading: false,
};

export const notificationPreferenceReducer = createReducer<NotificationPreferenceState>(
  initialState,

  on(getNotificationPreferences, (state): NotificationPreferenceState => {
    return {
      ...state,
      notificationPreferences: null,
      isLoading: true,
      error: null,
    };
  }),

  on(getNotificationPreferencesSuccess, (state, { preferences }): NotificationPreferenceState => {
    return {
      ...state,
      notificationPreferences: preferences,
      error: null,
      isLoading: false,
    };
  }),

  on(getNotificationPreferencesFailure, (state, { error }): NotificationPreferenceState => {
    return {
      ...state,
      notificationPreferences: null,
      error,
      isLoading: false,
    };
  }),

  on(saveNotificationPreferences, (state): NotificationPreferenceState => {
    return {
      ...state,
      // cleared on submit, or a corrected retry shows the previous rejection for the whole request
      saveError: null,
      isSaving: true,
    };
  }),

  on(saveNotificationPreferencesSuccess, (state, { preferences }): NotificationPreferenceState => {
    return {
      ...state,
      notificationPreferences: preferences,
      error: null,
      saveError: null,
      isSaving: false,
    };
  }),

  on(saveNotificationPreferencesFailure, (state, { error }): NotificationPreferenceState => {
    return {
      ...state,

      // preferences are deliberately kept: a rejected save changed nothing server-side, and the
      // user has to be able to correct the named field and submit again
      saveError: error,
      isSaving: false,
    };
  }),

  on(getNotificationTypes, (state): NotificationPreferenceState => {
    return { ...state, isLoading: true, notificationTypes: null };
  }),

  on(getNotificationTypesSuccess, (state, { notificationTypes }): NotificationPreferenceState => {
    return {
      ...state,
      notificationTypes,
      error: null,
      isLoading: false,
    };
  }),

  on(getNotificationTypesFailure, (state, { error }): NotificationPreferenceState => {
    return {
      ...state,
      notificationTypes: null,
      error,
      isLoading: false,
    };
  })
);
